import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { until } from "lit/directives/until.js";

import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";

import { styles } from "./PlaceListEditor.style";
import { FormsT } from "../form/FormTypes";
import {
    IPlaceList,
    IPlaceListEntry,
    IPlaceListTable,
    makeEmptyPlaceListEntry,
} from "../../backend/IPlaceListTable";
import { litIf } from "../LitUtils";
import { IPlaceTable } from "../../backend/IPlaceTable";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { showConfirmDialog } from "../ConfirmDialog";
import "../ProgressSpinner/ProgressSpinner";
import { watch } from "../internal/watch";
import { DATASTORE_SAVE_WAIT_TIME } from "../../Constants";
import { Mutex } from "async-mutex";
import { showPlaceListEntryDialog } from "../PlaceListPicker/PlaceListPicker";
import { GeoLocation } from "../../GeoLocation";
import { distanceInBetweenLocationsMeters, ILocation } from "../../models/ILocation";
import { orderBy } from "lodash-es";
import { IPlace } from "../../../main";
import { eachLimit } from "async";

export const PlaceListFields: FormsT[] = [
    {
        type: "textInput",
        title: "Name",
        property: "name",
        placeholder: "Enter list name",
    },
    {
        type: "textInput",
        title: "Description",
        property: "description",
        placeholder: "Enter description",
        helpMsg: "Description of list",
    },
];

type IExtendedPlaceListEntry = {
    entry: IPlaceListEntry;
    place?: IPlace;
    distMeters?: number;
};
@customElement("ds-place-list-editor")
export class PlaceListEditor extends BaseElement implements ChangeSetListener {
    static override styles = [styles, LightTheme];

    @property({ type: Object }) placeList?: IPlaceList;
    @property({ type: Boolean }) noHeader = false;
    @property({ type: Boolean }) noFooter = false;

    @state() protected _currentEntries?: IExtendedPlaceListEntry[];
    @state() protected _editingList = false;
    @state() protected _loading = false;
    @state() protected _sortByDistance = false;

    private _placeTable = this._kernel.dataStore.getSource<IPlaceTable>("places");
    private _placeListTable = this._kernel.dataStore.getSource<IPlaceListTable>("place-list");
    private _geoLocation: GeoLocation = this._kernel.geoLocation;
    private _savingTimeout?: any;
    private _updateMutex = new Mutex();

    @watch("placeList")
    async _onListChanged() {
        this._currentEntries = undefined;

        if (this.placeList) {
            await this._updateCurrentEntries();
            if (!this._editingList) {
                this._editingList = !this.placeList.name || this.placeList.name.length === 0;
            }
        }
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._placeListTable.addListener(this, []);
        this._subscribeToKernelEvent("ds-location-changed", (location: ILocation) => {
            this._resortPlaces(location);
        });
    }

    public override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this._sortByDistance) {
            this._geoLocation.stop();
            this._sortByDistance = false;
        }
    }

    onChangeSet(provider: DataProvider, _changeSet: ChangeSet<any>): void {
        if (provider.name === "place-list-entries") {
            this._updateCurrentEntries();
        }
    }

    private _startSavingTimeout() {
        this._savingTimeout = setTimeout(() => {
            this._loading = true;
        }, DATASTORE_SAVE_WAIT_TIME);
    }

    private _stopSavingTimeout() {
        if (this._savingTimeout) {
            clearTimeout(this._savingTimeout);
            this._savingTimeout = undefined;
        }
        this._loading = false;
    }

    async _updateCurrentEntries() {
        await this._updateMutex.runExclusive(async () => {
            if (this.placeList) {
                this._startSavingTimeout();
                let all = (
                    await this._kernel.dataStore
                        .getSource<IPlaceListTable>("place-list")
                        .getEntries(this.placeList.id)
                ).map((entry) => {
                    return {
                        entry,
                    } as IExtendedPlaceListEntry;
                });

                await eachLimit(all, 8, async (x) => {
                    x.place = await this._placeTable.read(x.entry.place);
                });

                all = all.filter((x) => x.place);
                this._stopSavingTimeout();

                all = [
                    ...all.filter((x) => !x.entry.checked),
                    ...all.filter((x) => x.entry.checked),
                ];

                this._currentEntries = all;
            }
        });
    }

    async _onSelected(list: IPlaceList) {
        this.placeList = list;
        this._raiseEvent("ds-list-changed", { list });
    }

    private async _saveList(list: IPlaceList) {
        if (this.placeList) {
            this._startSavingTimeout();
            await this._placeListTable.update(list);
            this._stopSavingTimeout();

            this._editingList = false;
        }
    }

    private async _addEntry(placeId: string) {
        await this._placeListTable.createEntry(
            this.placeList!.id,
            makeEmptyPlaceListEntry({
                place: placeId,
            })
        );
        await this._updateCurrentEntries();
    }

    private async _saveEntry(entry: IPlaceListEntry) {
        if (this.placeList) {
            this._startSavingTimeout();
            await this._placeListTable.updateEntry(entry);

            this._stopSavingTimeout();
            await this._updateCurrentEntries();
            this.requestUpdate();
        }
    }

    private _deleteEntry(entry: IPlaceListEntry) {
        if (this.placeList) {
            showConfirmDialog(
                {
                    open: true,
                    prompt: "Delete entry?",
                    message:
                        "This will irretrievably delete the entry. Press 'Yes' to delete or 'Cancel' to abort.",
                },
                async () => {
                    await this._placeListTable.deleteEntry(entry.id);
                    this._currentEntries = this._currentEntries?.filter(
                        (x) => x.entry.id !== entry.id
                    );
                },
                () => {}
            );
        }
    }

    private async _updateSortByDistance(newState: boolean) {
        if (newState) {
            if (!this._sortByDistance) {
                this._geoLocation.start();
                this._sortByDistance = true;
            }
        } else {
            if (this._sortByDistance) {
                this._geoLocation.stop();
                this._updateCurrentEntries();
                this._sortByDistance = false;
            }
        }
    }

    private _resortPlaces(location: ILocation) {
        this._currentEntries = orderBy(
            this._currentEntries?.map((x) => {
                x.distMeters = distanceInBetweenLocationsMeters(
                    location,
                    x.place?.data.location as ILocation
                );
                return x;
            }),
            ["distMeters"],
            ["asc"]
        );
    }

    private _renderEntry(entry: IExtendedPlaceListEntry) {
        const renderEntry = async (extEntry: IExtendedPlaceListEntry) => {
            const entry = extEntry.entry;
            const place = extEntry.place;
            if (place) {
                const inner = html`<div class="flex-row ${entry.checked ? "checked" : "unchecked"}">
                    <sl-checkbox
                        .checked=${!!entry.checked}
                        @sl-change=${(e: any) => {
                            entry.checked = e.target.checked;
                            this._saveEntry(entry);
                        }}
                    ></sl-checkbox>
                    <span
                        @click=${() =>
                            this._kernel.events.raiseEvent("ds-place-selected", entry.place)}
                        >${place.data.name}
                        ${this._sortByDistance
                            ? html`<ds-distance
                                  autoUnits
                                  decimals="1"
                                  .distance=${extEntry.distMeters}
                              ></ds-distance>`
                            : html``}</span
                    >
                </div>`;

                return html`<div class="entry flex-row">
                    ${inner}
                    <div class="actions">
                        <sl-tooltip content="Delete entry">
                            <sl-icon-button
                                @click=${() => this._deleteEntry(entry)}
                                name="trash3"
                            ></sl-icon-button>
                        </sl-tooltip>
                        <sl-tooltip content="Navigate to place">
                            <sl-icon-button
                                @click=${async () => {
                                    const place = await this._placeTable.read(entry.place);
                                    if (place) {
                                        this._kernel.events.raiseEvent("ds-navigate", {
                                            location: place.data.location,
                                        });
                                    }
                                }}
                                name="send"
                            ></sl-icon-button>
                        </sl-tooltip>
                        <sl-tooltip content="View details">
                            <sl-icon-button
                                @click=${() =>
                                    this._kernel.events.raiseEvent(
                                        "ds-place-selected",
                                        entry.place
                                    )}
                                name="eye"
                            ></sl-icon-button>
                        </sl-tooltip>
                        <sl-tooltip content="View on map">
                            <sl-icon-button
                                @click=${() =>
                                    this._raiseEvent("ds-view-on-map", {
                                        location: place.data.location,
                                    })}
                                name="pin-map-fill"
                            ></sl-icon-button>
                        </sl-tooltip>
                    </div>
                </div>`;
            }
            return html``;
        };

        return until(renderEntry(entry), html`<sl-spinner></sl-spinner>`);
    }

    private _renderListHeader() {
        return litIf(
            this.placeList,
            () => html`
                <div class="flex-column" slot="header">
                    <div class="flex-row list-header">
                        ${litIf(
                            this._editingList,
                            () =>
                                html`<ds-form
                                    .object=${this.placeList}
                                    .fields=${PlaceListFields}
                                ></ds-form>`,
                            () =>
                                html`<div class="flex-column">
                                    <h3>
                                        ${!this.placeList?.name || this.placeList.name.length === 0
                                            ? "Unnamed"
                                            : this.placeList.name}
                                    </h3>
                                    <span>${this.placeList!.description}</span>
                                </div>`
                        )}

                        <div class="actions">
                            ${litIf(
                                this._editingList,
                                () =>
                                    html`<sl-icon-button
                                            @click=${() => (this._editingList = false)}
                                            name="x-lg"
                                        ></sl-icon-button>
                                        <sl-icon-button
                                            @click=${() => {
                                                this._saveList(this.placeList!);
                                            }}
                                            name="check-lg"
                                        ></sl-icon-button>`,
                                () =>
                                    html` <sl-icon-button
                                        @click=${() => {
                                            this._editingList = true;
                                        }}
                                        name="pencil-fill"
                                    ></sl-icon-button>`
                            )}
                        </div>
                    </div>
                    <ds-search-bar
                        ?disabled=${!this.placeList}
                        excludeLocationResults
                        excludeSearchResults
                        placeholder="Type the name of place to add to the list"
                        @ds-view-on-map=${async (e: CustomEvent) => {
                            this._addEntry(e.detail.placeId);
                            e.stopPropagation();
                        }}
                    ></ds-search-bar>
                    <sl-switch
                        style="justify-items: flex-end;"
                        .checked=${this._sortByDistance}
                        @sl-change=${(e: any) => {
                            this._updateSortByDistance(e.target.checked);
                        }}
                        >Sort by distance</sl-switch
                    >
                </div>
            `
        );
    }

    override render() {
        return html`<div class="container flex-column ds-box-shadow" part="body">
            ${litIf(
                !this.noHeader,
                () => html`
                    <ds-place-list-picker
                        layout="dropdown"
                        .userId=${this._kernel.user?.id}
                        @ds-selected=${(e: CustomEvent) => this._onSelected(e.detail.list)}
                    ></ds-place-list-picker>
                `
            )}
            <ds-fixed-panel>
                ${this._renderListHeader()}
                <div slot="body" class="body">
                    ${litIf(
                        this._loading || this._currentEntries,
                        () => html`
                            ${litIf(
                                this._loading,
                                () => html`<ds-progress-spinner></ds-progress-spinner>`,
                                () =>
                                    html`${this._currentEntries!.map((x) => this._renderEntry(x))}`
                            )}
                        `
                    )}
                </div>
                ${litIf(
                    !this.noFooter,
                    () =>
                        html` <div slot="footer" class="footer flex-row">
                            <sl-button
                                pill
                                size="medium"
                                @click=${() =>
                                    showConfirmDialog(
                                        {
                                            open: true,
                                            prompt: "Delete list?",
                                            message:
                                                "This will irretrievably delete the list. Press 'Yes' to delete or 'Cancel' to abort.",
                                        },
                                        async () => {
                                            await this._placeListTable.delete(this.placeList!.id);
                                            this.placeList = undefined;
                                        },
                                        () => {}
                                    )}
                                >Delete</sl-button
                            >
                            <sl-button
                                pill
                                size="medium"
                                @click=${async () => {
                                    showPlaceListEntryDialog();
                                }}
                                >Create new list</sl-button
                            >
                        </div>`
                )}
            </ds-fixed-panel>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-place-list-editor": PlaceListEditor;
    }
}
